import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer,
} from 'recharts'
import { supabase } from '../supabase'
import { invPaid, invBalance, invDate } from '../lib/billing'

// ─── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  teal900: '#0d3d3d', teal700: '#126868', teal600: '#148080',
  teal500: '#19a8a8', teal400: '#2ecece', teal200: '#90e0e0',
  teal100: '#ccf2f2', teal50: '#edfafa',
  white: '#ffffff',
  gray50: '#f8fafc', gray100: '#f1f5f9', gray200: '#e2e8f0',
  gray300: '#cbd5e1', gray400: '#94a3b8', gray500: '#64748b',
  gray600: '#475569', gray700: '#334155', gray900: '#0f172a',
  red50: '#fff1f2', red600: '#dc2626',
  green50: '#f0fdf4', green600: '#16a34a',
  amber50: '#fffbeb', amber600: '#d97706',
  purple600: '#9333ea',
  font: "'Segoe UI', system-ui, -apple-system, sans-serif",
}

const CHART_COLORS = ['#19a8a8','#148080','#8b5cf6','#f59e0b','#ef4444','#06b6d4','#10b981','#f97316','#3b82f6','#ec4899']
const STATUS_COLORS = { Completed: '#16a34a', Confirmed: '#19a8a8', Scheduled: '#f59e0b', Cancelled: '#dc2626', 'No Show': '#f97316', 'In Treatment': '#3b82f6' }

// ─── Helpers ───────────────────────────────────────────────────────────────────
function isoDate(d = new Date()) { return d.toISOString().split('T')[0] }
function fmtCur(n) { return '₹' + (Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 }) }
function fmtDate(d) { return d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '' }
function fmtPct(n) { return (Number(n) || 0).toFixed(1) + '%' }

function getMondayOfWeek(d) {
  const dt = new Date(d); const day = dt.getDay()
  dt.setDate(dt.getDate() + (day === 0 ? -6 : 1 - day)); dt.setHours(0,0,0,0); return dt
}

function getRange(filter, custom) {
  const now = new Date()
  if (filter === 'today')     return { start: isoDate(now), end: isoDate(now) }
  if (filter === 'week')      { const m = getMondayOfWeek(now); const s = new Date(m); s.setDate(s.getDate()+6); return { start: isoDate(m), end: isoDate(s) } }
  if (filter === 'month')     { return { start: isoDate(new Date(now.getFullYear(), now.getMonth(), 1)), end: isoDate(new Date(now.getFullYear(), now.getMonth()+1, 0)) } }
  if (filter === 'lastmonth') { return { start: isoDate(new Date(now.getFullYear(), now.getMonth()-1, 1)), end: isoDate(new Date(now.getFullYear(), now.getMonth(), 0)) } }
  if (filter === 'custom')    return custom
  return { start: isoDate(now), end: isoDate(now) }
}

function daysBetween(start, end) {
  const days = []; let d = new Date(start)
  const e = new Date(end)
  while (d <= e) { days.push(isoDate(d)); d.setDate(d.getDate()+1) }
  return days
}

const QUICK_FILTERS = [
  { key: 'today',     label: 'Today'      },
  { key: 'week',      label: 'This Week'  },
  { key: 'month',     label: 'This Month' },
  { key: 'lastmonth', label: 'Last Month' },
  { key: 'custom',    label: 'Custom'     },
]

// ─── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, icon, accent, large }) {
  return (
    <div style={{
      background: T.white, borderRadius: 12, padding: '18px 20px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.06)', borderLeft: `4px solid ${accent}`,
      display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 11.5, fontWeight: 600, color: T.gray400, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</span>
        <span style={{ fontSize: 18, width: 34, height: 34, borderRadius: 9, background: accent + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</span>
      </div>
      <div style={{ fontSize: large ? 32 : 26, fontWeight: 800, color: T.gray900, lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: T.gray400 }}>{sub}</div>}
    </div>
  )
}

// ─── Chart card wrapper ────────────────────────────────────────────────────────
function ChartCard({ title, sub, children, flex, minH = 260 }) {
  return (
    <div style={{
      background: T.white, borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
      flex: flex || 1, minWidth: 0, overflow: 'hidden',
    }}>
      <div style={{ padding: '16px 20px 8px', borderBottom: `1px solid ${T.gray100}` }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.gray900 }}>{title}</div>
        {sub && <div style={{ fontSize: 11.5, color: T.gray400, marginTop: 2 }}>{sub}</div>}
      </div>
      <div style={{ padding: '12px 16px', minHeight: minH }}>
        {children}
      </div>
    </div>
  )
}

// ─── Custom tooltip ────────────────────────────────────────────────────────────
function CustomTooltip({ active, payload, label, currency }) {
  if (!active || !payload?.length) return null
  return (
    <div style={{ background: T.white, border: `1px solid ${T.gray200}`, borderRadius: 8, padding: '8px 12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontFamily: T.font, fontSize: 12 }}>
      <div style={{ fontWeight: 700, color: T.gray700, marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color, fontWeight: 600 }}>
          {p.name}: {currency ? fmtCur(p.value) : p.value}
        </div>
      ))}
    </div>
  )
}

// ─── Section header ────────────────────────────────────────────────────────────
function SectionHeader({ icon, title }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, marginTop: 4 }}>
      <span style={{ fontSize: 20 }}>{icon}</span>
      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: T.gray900 }}>{title}</h3>
      <div style={{ flex: 1, height: 1, background: T.gray200, marginLeft: 6 }} />
    </div>
  )
}

// ─── Empty chart placeholder ───────────────────────────────────────────────────
function EmptyChart({ msg = 'No data for this period' }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, color: T.gray300 }}>
      <div style={{ fontSize: 36, marginBottom: 8 }}>📊</div>
      <div style={{ fontSize: 13, color: T.gray400 }}>{msg}</div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// REPORTS MODULE
// ═══════════════════════════════════════════════════════════════════════════════
export default function Reports() {
  const [filter, setFilter]       = useState('month')
  const [custom, setCustom]       = useState({ start: isoDate(), end: isoDate() })
  const [loading, setLoading]     = useState(true)
  const [data, setData]           = useState(null)
  const printRef = useRef(null)

  const range = getRange(filter, filter === 'custom' ? custom : null)

  const fetchAll = useCallback(async () => {
    if (!range?.start || !range?.end) return
    setLoading(true)

    const [invoicesRes, appointmentsRes, patientsRes, inventoryRes] = await Promise.all([
      supabase.from('invoices').select('*').gte('created_at', range.start + 'T00:00:00+05:30').lte('created_at', range.end + 'T23:59:59.999+05:30'),
      supabase.from('appointments').select('*, patients(full_name)').gte('date', range.start).lte('date', range.end),
      supabase.from('patients').select('id, created_at'),
      supabase.from('inventory').select('*'),
    ])

    const invoices     = invoicesRes.data     || []
    const appointments = appointmentsRes.data || []
    const patients     = patientsRes.data     || []
    const inventory    = inventoryRes.data    || []

    // ── REVENUE ────────────────────────────────────────────────────────────────
    const totalRev    = invoices.reduce((s, i) => s + Number(i.total || 0), 0)
    // Collected includes partial payments; pending is the balance still due (lowercase statuses)
    const collected   = invoices.reduce((s, i) => s + invPaid(i), 0)
    const pending     = invoices.reduce((s, i) => s + invBalance(i), 0)
    const days        = daysBetween(range.start, range.end)
    const avgPerDay   = days.length ? totalRev / days.length : 0

    // Daily revenue
    const dailyRevMap = {}
    invoices.forEach(i => {
      const d = invDate(i)
      if (d) dailyRevMap[d] = (dailyRevMap[d] || 0) + Number(i.total || 0)
    })
    const dailyRevenue = days.map(d => ({ date: fmtDate(d), revenue: Math.round(dailyRevMap[d] || 0) }))

    // Payment mode
    const modeMap = {}
    invoices.forEach(i => { if (i.payment_mode) modeMap[i.payment_mode] = (modeMap[i.payment_mode] || 0) + Number(i.total || 0) })
    const paymentModes = Object.entries(modeMap).map(([name, value]) => ({ name, value: Math.round(value) })).sort((a,b) => b.value - a.value)

    // Revenue by service (via appointments joined)
    const apptMap = {}
    appointments.forEach(a => { if (a.service_type) apptMap[a.id] = a.service_type })
    const serviceRevMap = {}
    invoices.forEach(i => {
      const svc = i.appointment_id ? apptMap[i.appointment_id] || 'General' : 'General'
      serviceRevMap[svc] = (serviceRevMap[svc] || 0) + Number(i.total || 0)
    })
    const revenueByService = Object.entries(serviceRevMap)
      .map(([name, value]) => ({ name, value: Math.round(value) }))
      .sort((a,b) => b.value - a.value).slice(0, 8)

    // ── PATIENTS ───────────────────────────────────────────────────────────────
    const allPatientsInRange = patients.filter(p => {
      const d = p.created_at?.split('T')[0]
      return d >= range.start && d <= range.end
    })
    const newPatients      = allPatientsInRange.length
    const returningPatients = appointments.filter(a => {
      const p = patients.find(p => p.id === a.patient_id)
      return p && p.created_at?.split('T')[0] < range.start
    }).map(a => a.patient_id).filter((v, i, arr) => arr.indexOf(v) === i).length

    // Monthly patient growth (last 6 months)
    const now = new Date()
    const monthlyGrowth = Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (5-i), 1)
      const start = isoDate(new Date(d.getFullYear(), d.getMonth(), 1))
      const end   = isoDate(new Date(d.getFullYear(), d.getMonth()+1, 0))
      const count = patients.filter(p => { const pd = p.created_at?.split('T')[0]; return pd >= start && pd <= end }).length
      return { month: d.toLocaleDateString('en-IN', { month: 'short' }), patients: count }
    })

    // Top 5 patients by visits
    const visitCount = {}
    appointments.forEach(a => { visitCount[a.patient_id] = (visitCount[a.patient_id] || 0) + 1 })
    const topPatients = Object.entries(visitCount)
      .sort((a,b) => b[1] - a[1]).slice(0, 5)
      .map(([id, visits]) => {
        const appt = appointments.find(a => a.patient_id === id)
        return { name: appt?.patients?.full_name?.split(' ')[0] || 'Patient', visits }
      })

    // ── APPOINTMENTS ───────────────────────────────────────────────────────────
    const totalAppts     = appointments.length
    const completed      = appointments.filter(a => a.status === 'Completed').length
    const cancelled      = appointments.filter(a => a.status === 'Cancelled').length
    const completionRate = totalAppts ? (completed / totalAppts) * 100 : 0
    const cancelRate     = totalAppts ? (cancelled / totalAppts) * 100 : 0
    const avgApptPerDay  = days.length ? totalAppts / days.length : 0

    // Daily appointments
    const dailyApptMap = {}
    appointments.forEach(a => { dailyApptMap[a.date] = (dailyApptMap[a.date] || 0) + 1 })
    const dailyAppts = days.map(d => ({ date: fmtDate(d), appointments: dailyApptMap[d] || 0 }))

    // Status breakdown
    const statusMap = {}
    appointments.forEach(a => { statusMap[a.status] = (statusMap[a.status] || 0) + 1 })
    const apptByStatus = Object.entries(statusMap).map(([name, value]) => ({ name, value }))

    // Peak hours
    const hourMap = {}
    appointments.forEach(a => {
      if (a.time) { const hr = parseInt(a.time.split(':')[0], 10); hourMap[hr] = (hourMap[hr] || 0) + 1 }
    })
    const peakHours = Array.from({ length: 11 }, (_, i) => i + 9).map(h => ({
      hour: h > 12 ? `${h-12}pm` : h === 12 ? '12pm' : `${h}am`,
      appointments: hourMap[h] || 0,
    }))

    // ── INVENTORY ──────────────────────────────────────────────────────────────
    const totalInvValue = inventory.reduce((s, i) => s + (Number(i.current_stock) * Number(i.purchase_price || 0)), 0)
    const totalSellValue = inventory.reduce((s, i) => s + (Number(i.current_stock) * Number(i.selling_price || 0)), 0)
    const lowStockItems = inventory.filter(i => Number(i.current_stock) <= Number(i.minimum_stock))
    const expiringSoon  = inventory.filter(i => {
      if (!i.expiry_date) return false
      const diff = (new Date(i.expiry_date) - new Date()) / (1000*60*60*24)
      return diff >= 0 && diff <= 30
    }).sort((a,b) => new Date(a.expiry_date) - new Date(b.expiry_date)).slice(0, 6)
    const stockByCategory = {}
    inventory.forEach(i => { stockByCategory[i.category] = (stockByCategory[i.category] || 0) + 1 })
    const categoryBreakdown = Object.entries(stockByCategory).map(([name, value]) => ({ name, value }))

    setData({
      revenue: { totalRev, collected, pending, avgPerDay, dailyRevenue, paymentModes, revenueByService },
      patients: { newPatients, returningPatients, monthlyGrowth, topPatients },
      appointments: { totalAppts, completed, cancelled, completionRate, cancelRate, avgApptPerDay, dailyAppts, apptByStatus, peakHours },
      inventory: { totalInvValue, totalSellValue, lowStockItems: lowStockItems.length, expiringSoon, categoryBreakdown, inventory },
    })
    setLoading(false)
  }, [range?.start, range?.end]) // eslint-disable-line

  useEffect(() => { fetchAll() }, [fetchAll])

  function handlePrint() { window.print() }

  const rangeLabel = range ? (range.start === range.end ? fmtDate(range.start) : `${fmtDate(range.start)} – ${fmtDate(range.end)}`) : ''

  return (
    <div ref={printRef} style={{ flex: 1, overflowY: 'auto', fontFamily: T.font, background: T.gray50 }}>

      {/* ── Top bar ── */}
      <div style={{ background: T.white, borderBottom: `1px solid ${T.gray200}`, padding: '16px 24px', position: 'sticky', top: 0, zIndex: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: T.gray900 }}>Reports</h2>
            {rangeLabel && <p style={{ margin: '2px 0 0', fontSize: 12, color: T.gray400 }}>{rangeLabel}</p>}
          </div>

          {/* Quick filters */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {QUICK_FILTERS.map(f => (
              <button key={f.key} onClick={() => setFilter(f.key)} style={{
                padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontFamily: T.font,
                border: `1.5px solid ${filter === f.key ? T.teal500 : T.gray200}`,
                background: filter === f.key ? T.teal600 : T.white,
                color: filter === f.key ? T.white : T.gray600,
                fontSize: 12.5, fontWeight: 600, transition: 'all 0.15s',
              }}>{f.label}</button>
            ))}
          </div>

          {/* Custom range */}
          {filter === 'custom' && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="date" value={custom.start} onChange={e => setCustom(c => ({ ...c, start: e.target.value }))}
                style={{ padding: '7px 10px', borderRadius: 7, border: `1.5px solid ${T.gray200}`, fontSize: 12.5, fontFamily: T.font, outline: 'none' }} />
              <span style={{ color: T.gray400, fontSize: 12 }}>to</span>
              <input type="date" value={custom.end} onChange={e => setCustom(c => ({ ...c, end: e.target.value }))}
                style={{ padding: '7px 10px', borderRadius: 7, border: `1.5px solid ${T.gray200}`, fontSize: 12.5, fontFamily: T.font, outline: 'none' }} />
            </div>
          )}

          {/* Print */}
          <button onClick={handlePrint} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8,
            border: `1.5px solid ${T.gray200}`, background: T.white, color: T.gray600,
            fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: T.font,
          }}>🖨️ Print / Export</button>
        </div>
      </div>

      {/* ── Content ── */}
      <div style={{ padding: '24px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '80px 0', color: T.gray400, fontSize: 14 }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
            Loading report data…
          </div>
        ) : !data ? null : (
          <>
            {/* ════════════════════════════════════════════════ REVENUE */}
            <SectionHeader icon="💰" title="Revenue" />

            {/* Stat cards */}
            <div style={{ display: 'flex', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
              <StatCard large label="Total Revenue" value={fmtCur(data.revenue.totalRev)} icon="💰" accent={T.teal500} sub={`${rangeLabel}`} />
              <StatCard label="Collected" value={fmtCur(data.revenue.collected)} icon="✅" accent={T.green600} sub="Amount received" />
              <StatCard label="Pending" value={fmtCur(data.revenue.pending)} icon="⏳" accent={T.amber600} sub="Balance due (unpaid + partial)" />
              <StatCard label="Avg / Day" value={fmtCur(data.revenue.avgPerDay)} icon="📅" accent={T.purple600} sub="Daily average" />
            </div>

            {/* Charts row 1 */}
            <div style={{ display: 'flex', gap: 14, marginBottom: 14, flexWrap: 'wrap' }}>
              <ChartCard title="Daily Revenue" sub={rangeLabel} flex={2}>
                {data.revenue.dailyRevenue.every(d => d.revenue === 0) ? <EmptyChart /> : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={data.revenue.dailyRevenue} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={T.gray100} />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: T.gray400 }} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: T.gray400 }} tickLine={false} axisLine={false} tickFormatter={v => '₹' + (v/1000).toFixed(0) + 'k'} />
                      <Tooltip content={<CustomTooltip currency />} />
                      <Bar dataKey="revenue" name="Revenue" fill={T.teal500} radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>

              <ChartCard title="Payment Mode" sub="Revenue split" flex={1}>
                {data.revenue.paymentModes.length === 0 ? <EmptyChart /> : (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={data.revenue.paymentModes} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`} labelLine={false}>
                        {data.revenue.paymentModes.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={v => fmtCur(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </div>

            {/* Revenue by service */}
            <div style={{ marginBottom: 24 }}>
              <ChartCard title="Revenue by Service Type" sub="Top services by revenue">
                {data.revenue.revenueByService.length === 0 ? <EmptyChart msg="No invoices linked to appointments yet" /> : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={data.revenue.revenueByService} layout="vertical" margin={{ top: 4, right: 24, left: 80, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={T.gray100} horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11, fill: T.gray400 }} tickLine={false} tickFormatter={v => '₹' + (v/1000).toFixed(0) + 'k'} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: T.gray600 }} tickLine={false} width={80} />
                      <Tooltip content={<CustomTooltip currency />} />
                      <Bar dataKey="value" name="Revenue" fill={T.teal600} radius={[0,4,4,0]}>
                        {data.revenue.revenueByService.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </div>

            {/* ════════════════════════════════════════════════ PATIENTS */}
            <SectionHeader icon="👤" title="Patients" />

            <div style={{ display: 'flex', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
              <StatCard label="New Patients" value={data.patients.newPatients} icon="🆕" accent={T.teal500} sub={`Registered ${rangeLabel}`} />
              <StatCard label="Returning Patients" value={data.patients.returningPatients} icon="🔄" accent={T.purple600} sub="Visited this period" />
              <StatCard label="Total Appointments" value={data.appointments.totalAppts} icon="📅" accent={T.green600} sub="In this period" />
            </div>

            <div style={{ display: 'flex', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
              <ChartCard title="Patient Growth" sub="New patients per month (last 6 months)" flex={2}>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={data.patients.monthlyGrowth} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                    <defs>
                      <linearGradient id="tealGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={T.teal500} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={T.teal500} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={T.gray100} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: T.gray400 }} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: T.gray400 }} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="patients" name="New Patients" stroke={T.teal500} strokeWidth={2.5} fill="url(#tealGrad)" dot={{ fill: T.teal500, r: 4 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Top 5 Patients" sub="By appointments this period" flex={1}>
                {data.patients.topPatients.length === 0 ? <EmptyChart /> : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={data.patients.topPatients} layout="vertical" margin={{ top: 4, right: 24, left: 60, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={T.gray100} horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11, fill: T.gray400 }} tickLine={false} allowDecimals={false} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: T.gray600 }} tickLine={false} width={60} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="visits" name="Visits" radius={[0,4,4,0]}>
                        {data.patients.topPatients.map((_, i) => <Cell key={i} fill={CHART_COLORS[i]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </div>

            {/* ════════════════════════════════════════════════ APPOINTMENTS */}
            <SectionHeader icon="📅" title="Appointments" />

            <div style={{ display: 'flex', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
              <StatCard label="Total" value={data.appointments.totalAppts} icon="📋" accent={T.teal500} />
              <StatCard label="Completed" value={data.appointments.completed} icon="✅" accent={T.green600} sub={fmtPct(data.appointments.completionRate) + ' completion rate'} />
              <StatCard label="Cancelled" value={data.appointments.cancelled} icon="❌" accent={T.red600} sub={fmtPct(data.appointments.cancelRate) + ' cancellation rate'} />
              <StatCard label="Avg / Day" value={data.appointments.avgApptPerDay.toFixed(1)} icon="📊" accent={T.purple600} sub="Appointments per day" />
            </div>

            <div style={{ display: 'flex', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
              <ChartCard title="Daily Appointments" sub={rangeLabel} flex={2}>
                {data.appointments.dailyAppts.every(d => d.appointments === 0) ? <EmptyChart /> : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={data.appointments.dailyAppts} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={T.gray100} />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: T.gray400 }} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: T.gray400 }} tickLine={false} axisLine={false} allowDecimals={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="appointments" name="Appointments" fill={T.teal500} radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>

              <ChartCard title="Status Breakdown" sub="Appointment outcomes" flex={1}>
                {data.appointments.apptByStatus.length === 0 ? <EmptyChart /> : (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={data.appointments.apptByStatus} dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius={75} innerRadius={35}
                        label={({ name, value }) => `${value}`} labelLine={false}>
                        {data.appointments.apptByStatus.map((entry, i) => (
                          <Cell key={i} fill={STATUS_COLORS[entry.name] || CHART_COLORS[i]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </div>

            {/* Peak hours heatmap */}
            <div style={{ marginBottom: 24 }}>
              <ChartCard title="Peak Hours" sub="Appointments by hour of day">
                {data.appointments.peakHours.every(h => h.appointments === 0) ? <EmptyChart /> : (
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={data.appointments.peakHours} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={T.gray100} />
                      <XAxis dataKey="hour" tick={{ fontSize: 11, fill: T.gray400 }} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: T.gray400 }} tickLine={false} axisLine={false} allowDecimals={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="appointments" name="Appointments" radius={[4,4,0,0]}>
                        {data.appointments.peakHours.map((entry, i) => {
                          const max = Math.max(...data.appointments.peakHours.map(h => h.appointments))
                          const intensity = max ? entry.appointments / max : 0
                          return <Cell key={i} fill={intensity > 0.7 ? T.teal800 : intensity > 0.4 ? T.teal600 : intensity > 0 ? T.teal400 : T.gray200} />
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </div>

            {/* ════════════════════════════════════════════════ INVENTORY */}
            <SectionHeader icon="📦" title="Inventory" />

            <div style={{ display: 'flex', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
              <StatCard label="Total Items" value={data.inventory.inventory.length} icon="📦" accent={T.teal500} sub="Products in stock" />
              <StatCard label="Inventory Value" value={fmtCur(data.inventory.totalInvValue)} icon="💵" accent={T.green600} sub="At purchase price" />
              <StatCard label="Retail Value" value={fmtCur(data.inventory.totalSellValue)} icon="🏷️" accent={T.purple600} sub="At selling price" />
              <StatCard label="Low / Out of Stock" value={data.inventory.lowStockItems} icon="⚠️" accent={T.amber600} sub="Need restocking" />
            </div>

            <div style={{ display: 'flex', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
              <ChartCard title="Stock by Category" sub="Items per category" flex={1}>
                {data.inventory.categoryBreakdown.length === 0 ? <EmptyChart /> : (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={data.inventory.categoryBreakdown} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85}
                        label={({ name, value }) => `${value}`} labelLine>
                        {data.inventory.categoryBreakdown.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                      <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>

              <ChartCard title="Expiring Soon" sub="Within next 30 days" flex={2} minH={100}>
                {data.inventory.expiringSoon.length === 0 ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 120, color: T.gray400, fontSize: 13 }}>
                    ✅ No items expiring in the next 30 days
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: T.gray50 }}>
                        {['Product', 'Category', 'Stock', 'Expiry Date', 'Days Left'].map(h => (
                          <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: T.gray500, textTransform: 'uppercase', letterSpacing: '0.4px', borderBottom: `1px solid ${T.gray200}` }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.inventory.expiringSoon.map((item, i) => {
                        const daysLeft = Math.ceil((new Date(item.expiry_date) - new Date()) / (1000*60*60*24))
                        return (
                          <tr key={item.id} style={{ borderBottom: `1px solid ${T.gray100}`, background: i % 2 === 0 ? T.white : T.gray50 }}>
                            <td style={{ padding: '9px 12px', fontWeight: 600, color: T.gray900 }}>{item.name}</td>
                            <td style={{ padding: '9px 12px', color: T.gray500 }}>{item.category}</td>
                            <td style={{ padding: '9px 12px', color: T.gray600 }}>{item.current_stock} {item.unit}</td>
                            <td style={{ padding: '9px 12px', color: daysLeft <= 7 ? T.red600 : T.amber600, fontWeight: 600 }}>
                              {new Date(item.expiry_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </td>
                            <td style={{ padding: '9px 12px' }}>
                              <span style={{
                                background: daysLeft <= 7 ? '#fee2e2' : '#fef3c7',
                                color: daysLeft <= 7 ? T.red600 : T.amber600,
                                border: `1px solid ${daysLeft <= 7 ? '#fca5a5' : '#fde68a'}`,
                                borderRadius: 20, padding: '2px 10px', fontSize: 11.5, fontWeight: 700,
                              }}>{daysLeft}d</span>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                )}
              </ChartCard>
            </div>
          </>
        )}
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #root * { visibility: hidden; }
          .reports-print, .reports-print * { visibility: visible; }
        }
      `}</style>
    </div>
  )
}
